import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { updateDog, type Dog } from '@/api/entities/dogs';
import { ExtractFnReturnType } from '@/lib/react-query';
import { useCognitoAuth } from '@/hooks/useCognitoAuth';
import { GraphQLRequestError } from '@/api/graphql';

type UpdateDogParams = {
  id: string;
  input: Partial<Dog>;
};

type QueryFnType = typeof updateDog;

type UseUpdateDogOptions = {
  config?: Omit<
    UseMutationOptions<ExtractFnReturnType<QueryFnType>, GraphQLRequestError, UpdateDogParams>,
    'mutationFn'
  >;
};

export const useUpdateDog = ({ config }: UseUpdateDogOptions = {}) => {
  const queryClient = useQueryClient();
  const { getAccessToken } = useCognitoAuth();

  return useMutation<ExtractFnReturnType<QueryFnType>, GraphQLRequestError, UpdateDogParams>({
    ...config,
    mutationFn: async ({ id, input }: UpdateDogParams) => {
      return updateDog(id, input, getAccessToken);
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ['dogs'] });
      queryClient.invalidateQueries({ queryKey: ['dogs', variables.id] });
      config?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
